import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Zap } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("flodec75@gmail.com");
  const [password, setPassword] = useState("Prospector2026!");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Connexion réussie");
      } else {
        await register(email, password, name || email.split("@")[0]);
        toast.success("Compte créé");
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#09090b] grain">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-slate-800/80 relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />

        <div className="relative z-10 inline-flex items-center gap-4 bg-white rounded-2xl px-5 py-3 w-fit shadow-[0_0_40px_rgba(6,182,212,0.12)]">
          <img data-testid="login-logo" src="/logo.png" alt="PC UP — L'informatique à votre service" className="h-20 w-auto object-contain" />
        </div>

        <div className="relative z-10 space-y-6">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold text-slate-100 leading-tight tracking-tight">
            Trouvez vos <span className="text-cyan-400">prochains clients</span>
            <br />de dépannage informatique.
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed max-w-md">
            L'outil de prospection de PC UP : scraping ciblé par département, pipeline CRM Kanban, messages IA (Claude Sonnet 5 & GPT 5.6) et envoi d'emails intégré.
          </p>

          <div className="flex flex-wrap gap-2.5 pt-4">
            {["75 Paris", "92 Hauts-de-Seine", "93 Seine-St-Denis", "94 Val-de-Marne", "78 Yvelines", "91 Essonne", "95 Val-d'Oise", "77 Seine-et-Marne"].map((d) => (
              <span key={d} className="text-xs font-mono px-2.5 py-1 rounded-full bg-slate-900/80 border border-slate-800 text-slate-400">{d}</span>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-4 text-xs font-mono text-slate-500 uppercase tracking-widest">
          <div className="flex items-center gap-1.5"><Zap className="w-3 h-3 text-emerald-400" /> Live scraping</div>
          <div>·</div>
          <div>Claude Sonnet 5</div>
          <div>·</div>
          <div>GPT 5.6</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 inline-flex bg-white rounded-xl px-4 py-2">
            <img src="/logo.png" alt="PC UP" className="h-14 w-auto object-contain" />
          </div>

          <h2 className="font-heading text-3xl font-bold text-slate-100 mb-1">
            {mode === "login" ? "Bon retour." : "Créer un compte"}
          </h2>
          <p className="text-slate-500 text-sm mb-8">
            {mode === "login" ? "Connectez-vous pour accéder à votre CRM." : "Démarrez votre prospection en 30 secondes."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Nom</label>
                <input
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 w-full px-4 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 focus:border-cyan-500/60 focus:outline-none text-slate-100 placeholder:text-slate-600"
                  placeholder="Florian"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 w-full px-4 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 focus:border-cyan-500/60 focus:outline-none text-slate-100 placeholder:text-slate-600"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Mot de passe</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 w-full px-4 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 focus:border-cyan-500/60 focus:outline-none text-slate-100 placeholder:text-slate-600"
              />
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-slate-950 font-semibold transition-colors"
            >
              {loading ? "…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </button>

            <button
              type="button"
              data-testid="toggle-mode-button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="w-full text-sm text-slate-400 hover:text-cyan-400 transition-colors"
            >
              {mode === "login" ? "Pas encore inscrit ? Créer un compte" : "Déjà un compte ? Se connecter"}
            </button>
          </form>

          <div className="mt-8 p-4 rounded-lg bg-slate-900/40 border border-slate-800">
            <div className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-2">Compte démo</div>
            <div className="text-xs text-slate-400 font-mono">flodec75@gmail.com · Prospector2026!</div>
          </div>
        </div>
      </div>
    </div>
  );
}
